import { CashBackMembership, Notification } from './';
import { NydusRecipient } from './nydus-recipient.type';

// We need OrderItemBase to be able to extend it in the concrete OrderItem class
// without having to duplicate the properties
export interface OrderItemBase {
  id: string;
  type: OrderItemType;
  attributes?: OrderItemAttributes;
  notifications: Notification[];
}

export enum OrderItemStatus {
  Cancelled = 'cancelled',
  Pending = 'pending',
  Processing = 'processing',
  Fulfilled = 'fulfilled',
  Failed = 'failed',
}

export enum OrderItemType {
  // BenefitOrderItem = 'benefit_order_item',
  CashRedemptionOrderItem = 'cash_redemption_order_item',
  // DonationOrderItem = 'donation_order_item',
  GiftCardOrderItem = 'gift_card_order_item',
  // ManualAdjustmentOrderItem = 'manual_adjustment_order_item',
  MerchandiseOrderItem = 'merchandise_order_item',
  PhysicalGiftCardOrderItem = 'physical_gift_card_order_item',
  PointsShareOrderItem = 'points_share_order_item',
  PointsTransferOrderItem = 'points_transfer_order_item',
  VoucherOrderItem = 'voucher_order_item',
  RetailOrderItem = 'retail_order_item',
  // PurchaseEraserOrderItem = 'purchase_eraser_order_item',
  // TravelBookingOrderItem = 'travel_booking_order_item',
}

// We need this union type so that we can utilize discriminated unions
// With that, we can narrow down the OrderItem type to a specific type
// https://www.typescriptlang.org/docs/handbook/unions-and-intersections.html#discriminating-unions

// If you create a new OrderItem type, you need to add it here
// And uncommented the corresponding OrderItemType above
export type OrderItem =
  | GiftCardOrderItem
  | PhysicalGiftCardOrderItem
  | CashRedemptionOrderItem
  | PointsTransferOrderItem
  | PointsShareOrderItem
  | MerchandiseOrderItem
  | VoucherOrderItem
  | RetailOrderItem;

export interface GiftCardOrderItem extends OrderItemBase {
  type: OrderItemType.GiftCardOrderItem;
  attributes: GiftCardOrderItemAttributes;
}

export interface PhysicalGiftCardOrderItem extends OrderItemBase {
  type: OrderItemType.PhysicalGiftCardOrderItem;
  attributes: PhysicalGiftCardOrderItemAttributes;
}

export interface CashRedemptionOrderItem extends OrderItemBase {
  type: OrderItemType.CashRedemptionOrderItem;
  attributes: CashRedemptionOrderItemAttributes;
}

export interface PointsTransferOrderItem extends OrderItemBase {
  type: OrderItemType.PointsTransferOrderItem;
  attributes: PointsTransferOrderItemAttributes;
}

export interface MerchandiseOrderItem extends OrderItemBase {
  type: OrderItemType.MerchandiseOrderItem;
  attributes: MerchandiseOrderItemAttributes;
}

export interface PointsShareOrderItem extends OrderItemBase {
  type: OrderItemType.PointsShareOrderItem;
  attributes: PointsShareOrderItemAttributes;
}

export interface VoucherOrderItem extends OrderItemBase {
  type: OrderItemType.VoucherOrderItem;
  attributes: VoucherOrderItemAttributes;
}

export interface RetailOrderItem extends OrderItemBase {
  type: OrderItemType.RetailOrderItem;
  attributes: RetailOrderItemAttributes;
}

export interface OrderItemAttributes {
  userId: string;
  status: OrderItemStatus;
  description: string;
  quantity: number;
  cashPaid: number;
  cashAmount: number;
  pointsPaid: number;
  pointsAmount: number;
  createdAt: string;
  updatedAt: string;
  data: OrderItemData;
}

export interface CashRedemptionOrderItemAttributes extends OrderItemAttributes {
  data: CashRedemptionOrderItemData;

  // Customized for cashback order on BNZ.
  customFields?: Record<string, string>;
}

export interface GiftCardOrderItemAttributes extends OrderItemAttributes {
  data: GiftCardOrderItemData;
}

export interface PhysicalGiftCardOrderItemAttributes
  extends OrderItemAttributes {
  data: PhysicalGiftCardOrderItemData;
}

export interface PointsTransferOrderItemAttributes extends OrderItemAttributes {
  data: PointsTransferOrderItemMetadata;
}

export interface MerchandiseOrderItemAttributes extends OrderItemAttributes {
  data: MerchandiseOrderItemData;
}

export interface PointsShareOrderItemAttributes extends OrderItemAttributes {
  data: PointsShareOrderItemMetadata;
}
// Shared with PointsTransactionMetadata implementation, just used for PT and My Orders page.

export interface VoucherOrderItemAttributes extends OrderItemAttributes {
  data: VoucherOrderItemMetadata;
}

export interface RetailOrderItemAttributes extends OrderItemAttributes {
  data: RetailOrderItemMetadata;
}

interface MembershipInformation {
  firstName: string;
  lastName: string;
  number: string;
  dateOfBirth?: string;
}

interface VoucherMembershipInformation {
  id: string;
  firstName: string;
  lastName: string;
  number: string;
}

// Metadata, arrange alphabetically

export interface CashRedemptionOrderItemData {
  amount: number;
  amountInCents: number;
  currency: string;
  currencyShortName: string;
  loyaltyProgramId: string;
  membership?: CashBackMembership;
  partnerUserId: string;
  programShortName: string;
  tenantCurrencyShortName: string;
  promoCode?: string;
  processingDelay?: number;
}

export interface OrderAddress {
  city: string | null;
  countryCode: string | null;
  line1: string | null;
  line2: string | null;
  postalCode: string | null;
  state: string | null;
}

export interface DigitalGiftCardFulfillmentData {
  downloadUrl: string;
  fulfillmentUrl: string;
  receiptCode: string;
  redemptionUrl: string;
  viewUrl: string;
}

export interface PhysicalGiftCardFulfillmentData {
  shippedStatus: string;
  trackingId: string;
  trackingUrl: string;
}

interface BaseGiftCardOrderItemData<TFulfillmentData> {
  amount: number;
  cardId: string;
  currency: string;
  productName: string;
  productImageUrl: string;
  supplier: string;
  displayValue: number;
  supplierReferenceId: string;
  recipient: {
    address: OrderAddress | null;
    companyName: string;
    message: string;
    lastName: string;
    firstName: string;
    phoneNumber: string;
    emailAddress: string;
  };
  fulfillmentData: TFulfillmentData[];
  fulfillmentUrl: string;
}
export type GiftCardOrderItemData =
  BaseGiftCardOrderItemData<DigitalGiftCardFulfillmentData>;
export type PhysicalGiftCardOrderItemData =
  BaseGiftCardOrderItemData<PhysicalGiftCardFulfillmentData>;

export interface MerchandiseOrderItemData {
  amount: number;
  cashAmount: number;
  currency: string;
  displayValue: number;
  externalSupplierOrderReference: string;
  fulfillmentData: PhysicalGiftCardFulfillmentData;
  merchandiseId: string;
  orderSourceType: string;
  pointsPercentage: number;
  productImageUrl: string;
  productName: string;
  quantity: number;
  recipient: NydusRecipient;
  sku: string;
  supplier: string;
}

// This shares the same shape roughly as PointsTransactionMetadata.
// That will be removed when we completely migrate to Orders.

export interface PointsTransferOrderItemMetadata {
  loyaltyCurrencyName: string;
  loyaltyCurrencyShortName: string;
  loyaltyProgramId: string;
  membershipInformation: MembershipInformation;
  productName: string; // Loyalty program name
  programShortName: string; // Loyalty program short name
  tenantCurrencyName: string;
  tenantCurrencyShortName: string;
  transferAmount: number;
  promoCode?: string;
  transferConnectIdentifier: string;
}

export interface PointsShareOrderItemMetadata {
  recipientUserId: string;
  senderUserId: string;
  transferAmount: number;
  recipientFirstName: string;
  recipientLastName: string;
  recipientFullName: string;
  recipientDisplayName: string;
  senderFirstName: string;
  senderLastName: string;
}

export interface VoucherOrderItemMetadata
  extends PointsTransferOrderItemMetadata {
  membershipInformation: VoucherMembershipInformation;
}

export interface RetailOrderItemMetadata
  extends PointsTransferOrderItemMetadata {}

export type OrderItemData =
  | CashRedemptionOrderItemData
  | GiftCardOrderItemData
  | PhysicalGiftCardOrderItemData
  | MerchandiseOrderItemData
  | PointsShareOrderItemMetadata
  | PointsTransferOrderItemMetadata
  | VoucherOrderItemMetadata
  | RetailOrderItemMetadata;
